import { supabase } from '@/integrations/supabase/client';

/**
 * Resolves a storage reference to a signed URL.
 * Handles both:
 * - Raw file paths (e.g., "org-id/logo-uuid.png") — generates a signed URL
 * - Legacy full URLs (e.g., "https://...supabase.co/.../organization-assets/...") — extracts path, then signs
 * 
 * @param bucket The storage bucket name
 * @param pathOrUrl A file path or a legacy full URL stored in the DB
 * @param expiresIn Signed URL duration in seconds (default: 1 hour)
 * @returns The signed URL or null if resolution fails
 */
export async function getSignedStorageUrl(
  bucket: string,
  pathOrUrl: string | null | undefined,
  expiresIn: number = 3600
): Promise<string | null> {
  if (!pathOrUrl) return null;

  // Extract file path from a full URL if needed
  let filePath = pathOrUrl;
  const bucketMarker = `/${bucket}/`;
  
  // Handle public URL format: .../storage/v1/object/public/{bucket}/...
  const publicIdx = pathOrUrl.indexOf(`/storage/v1/object/public${bucketMarker}`);
  if (publicIdx !== -1) {
    filePath = pathOrUrl.substring(publicIdx + `/storage/v1/object/public${bucketMarker}`.length);
  }
  
  // Handle signed URL format: .../storage/v1/object/sign/{bucket}/...
  const signIdx = pathOrUrl.indexOf(`/storage/v1/object/sign${bucketMarker}`);
  if (signIdx !== -1) {
    const pathWithQuery = pathOrUrl.substring(signIdx + `/storage/v1/object/sign${bucketMarker}`.length);
    filePath = pathWithQuery.split('?')[0]; // Remove query params (token, etc.)
  }

  // If it still looks like a full URL but we couldn't parse it, try one more pattern
  if (filePath.startsWith('http')) {
    const lastBucketIdx = filePath.lastIndexOf(`${bucket}/`);
    if (lastBucketIdx !== -1) {
      filePath = filePath.substring(lastBucketIdx + `${bucket}/`.length).split('?')[0];
    } else {
      console.warn(`Cannot extract path from URL for bucket "${bucket}":`, pathOrUrl);
      return null;
    }
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(filePath, expiresIn);

  if (error || !data?.signedUrl) {
    console.warn(`Failed to create signed URL for ${bucket}/${filePath}:`, error);
    return null;
  }

  return data.signedUrl;
}

/**
 * Extracts the storage file path from a full URL or returns the path as-is.
 */
export function extractStoragePath(bucket: string, pathOrUrl: string): string {
  if (!pathOrUrl.startsWith('http')) return pathOrUrl;
  
  const markers = [
    `/storage/v1/object/public/${bucket}/`,
    `/storage/v1/object/sign/${bucket}/`,
    `${bucket}/`,
  ];
  
  for (const marker of markers) {
    const idx = pathOrUrl.indexOf(marker);
    if (idx !== -1) {
      return pathOrUrl.substring(idx + marker.length).split('?')[0];
    }
  }
  
  return pathOrUrl;
}
